/**
 * Importación masiva de entidades desde un Excel.
 *
 * Tres pasos, los mismos que el sorteo usa con el padrón y por las mismas
 * razones: **subir**, **confirmar el mapeo de columnas** y **ver qué saldría
 * antes de aplicarlo**. Un listado real trae cientos de filas y confirmarlo a
 * ciegas es cómo se llenan los catálogos de basura.
 *
 * `SD-3`: el archivo lo arma cada área a su manera, así que la adivinación de
 * columnas solo PROPONE y aquí se corrige.
 *
 * Lo rechazado **se enseña con su motivo y su número de fila**, nunca se
 * descarta en silencio: es lo que permite arreglar el archivo y reintentar.
 */
import { useState } from 'react'
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
} from '@mpfn/ui'
import {
  Callout,
  DataTable,
  FileUploadField,
  FormField,
  StatCard,
  type DataTableColumn,
} from '@mpfn/patterns'
import { Upload } from 'lucide-react'
import {
  useCargarListadoEntidades,
  useConfirmarImportacionEntidades,
  usePrevisualizarEntidades,
} from '../api/consultas.js'
import type {
  FilaRechazadaEntidad,
  LecturaEntidades,
  MapeoEntidad,
  PrevisualizacionEntidades,
} from '../api/tipos.js'
import { Casilla } from '../componentes/Conmutadores.js'
import { avisarError, avisarExito, plural } from '../lib/avisos.js'

/** Qué columnas se piden y cuáles no se pueden dejar en blanco. */
const COLUMNAS: ReadonlyArray<{ clave: keyof MapeoEntidad; etiqueta: string; obligatoria: boolean }> = [
  { clave: 'nombre', etiqueta: 'Nombre de la entidad', obligatoria: true },
  { clave: 'tipo', etiqueta: 'Tipo', obligatoria: true },
  { clave: 'siglas', etiqueta: 'Siglas', obligatoria: false },
  { clave: 'ruc', etiqueta: 'RUC', obligatoria: false },
  { clave: 'canal', etiqueta: 'Canal', obligatoria: false },
  { clave: 'destino', etiqueta: 'Destino del canal', obligatoria: false },
]

export function DialogoImportarEntidades({
  abierto,
  onCambio,
}: {
  abierto: boolean
  onCambio: (valor: boolean) => void
}) {
  const cargar = useCargarListadoEntidades()
  const previsualizar = usePrevisualizarEntidades()
  const confirmar = useConfirmarImportacionEntidades()

  const [archivo, setArchivo] = useState<File | null>(null)
  const [lectura, setLectura] = useState<LecturaEntidades | undefined>(undefined)
  const [mapeo, setMapeo] = useState<Partial<MapeoEntidad>>({})
  const [previo, setPrevio] = useState<PrevisualizacionEntidades | undefined>(undefined)
  const [crearTipos, setCrearTipos] = useState(false)

  const reiniciar = (): void => {
    setArchivo(null)
    setLectura(undefined)
    setMapeo({})
    setPrevio(undefined)
    setCrearTipos(false)
  }

  const listo = mapeo.nombre !== undefined && mapeo.tipo !== undefined
  const entrada = listo
    ? {
        mapeo: mapeo as MapeoEntidad,
        ...(lectura ? { hoja: lectura.hoja } : {}),
        crearTiposFaltantes: crearTipos,
      }
    : undefined

  // Cualquier cambio de mapeo invalida lo que se vio: se vuelve a pedir en
  // lugar de dejar en pantalla un recuento que ya no corresponde.
  const pedirPrevio = (siguiente: typeof entrada): void => {
    if (!siguiente) return
    previsualizar.mutate(siguiente, {
      onSuccess: setPrevio,
      onError: (error) => {
        avisarError(error, 'No se pudo previsualizar la importación')
      },
    })
  }

  const columnasRechazo: ReadonlyArray<DataTableColumn<FilaRechazadaEntidad>> = [
    { id: 'fila', header: 'Fila', cell: (r) => r.fila, align: 'right' },
    { id: 'motivo', header: 'Motivo', cell: (r) => <StatusBadge tone="warning" label={r.motivo} /> },
    {
      id: 'contenido',
      header: 'Lo que traía',
      hideOnMobile: true,
      cell: (r) => <span className="sigfisc-nota">{r.contenidoOriginal}</span>,
    },
  ]

  return (
    <Dialog
      open={abierto}
      onOpenChange={(valor) => {
        if (!valor) reiniciar()
        onCambio(valor)
      }}
    >
      <DialogContent className="sigfisc-dialogo--ancho">
        <DialogTitle>Importar entidades</DialogTitle>
        <DialogDescription>
          Un Excel con una entidad por fila. Solo el nombre y el tipo son obligatorios; si añade
          canal y destino, la entidad queda lista para requerir.
        </DialogDescription>

        <div className="sigfisc-formulario">
          <FormField
            id="listado-entidades"
            label="Listado en Excel"
            required
            hint="La primera fila con contenido se toma como cabecera."
          >
            <FileUploadField
              id="listado-entidades"
              value={archivo}
              onChange={(elegido) => {
                setArchivo(elegido)
                setLectura(undefined)
                setPrevio(undefined)
                if (!elegido) return
                cargar.mutate(
                  { archivo: elegido },
                  {
                    onSuccess: (datos) => {
                      setLectura(datos)
                      setMapeo(datos.mapeoPropuesto)
                      pedirPrevio(
                        datos.mapeoCompleto
                          ? {
                              mapeo: datos.mapeoPropuesto as MapeoEntidad,
                              hoja: datos.hoja,
                              crearTiposFaltantes: false,
                            }
                          : undefined,
                      )
                    },
                    onError: (error) => {
                      avisarError(error, 'No se pudo leer el archivo')
                    },
                  },
                )
              }}
              accept=".xlsx,.xls"
              acceptLabel="Excel"
              maxSizeMb={20}
              variant="compact"
              onError={(mensaje) => {
                avisarError(new Error(mensaje), 'El archivo no es válido')
              }}
            />
          </FormField>

          {lectura && (
            <>
              <p className="sigfisc-nota">
                {lectura.nombreArchivo} · hoja «{lectura.hoja}» ·{' '}
                {plural(lectura.totalFilas, 'fila', 'filas')}
              </p>

              <fieldset className="sigfisc-mapeo">
                <legend>Mapeo de columnas · el nombre y el tipo son obligatorios</legend>
                {COLUMNAS.map((columna) => (
                  <FormField
                    key={columna.clave}
                    id={`mapeo-entidad-${columna.clave}`}
                    label={columna.etiqueta}
                    required={columna.obligatoria}
                  >
                    <Select
                      value={mapeo[columna.clave] === undefined ? '' : String(mapeo[columna.clave])}
                      onValueChange={(valor) => {
                        const siguiente = { ...mapeo, [columna.clave]: Number(valor) }
                        setMapeo(siguiente)
                        setPrevio(undefined)
                        if (siguiente.nombre !== undefined && siguiente.tipo !== undefined) {
                          pedirPrevio({
                            mapeo: siguiente as MapeoEntidad,
                            hoja: lectura.hoja,
                            crearTiposFaltantes: crearTipos,
                          })
                        }
                      }}
                    >
                      <SelectTrigger id={`mapeo-entidad-${columna.clave}`}>
                        <SelectValue placeholder="Elija la columna" />
                      </SelectTrigger>
                      <SelectContent>
                        {lectura.cabeceras.map((cabecera, indice) => (
                          <SelectItem key={`${cabecera}-${String(indice)}`} value={String(indice)}>
                            {cabecera || `Columna ${String(indice + 1)}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>
                ))}
              </fieldset>

              {!listo && (
                <Callout tone="warning" title="Falta elegir columnas">
                  Indique al menos cuál es el nombre y cuál el tipo.
                </Callout>
              )}
            </>
          )}

          {previo && (
            <>
              {/* `StatCard` del DS, no tarjetas propias: es exactamente esta
                  pieza —una cifra con su rótulo— y `UI-5` dice que no se
                  reimplementa lo que el sistema ya trae. */}
              <div className="sigfisc-kpis">
                <StatCard label="Se crearán" value={previo.aCrear.length} />
                <StatCard label="Se rechazarán" value={previo.rechazadas.length} />
                <StatCard label="Tipos no encontrados" value={previo.tiposFaltantes.length} />
              </div>

              {previo.tiposFaltantes.length > 0 && (
                <Callout
                  tone="info"
                  title={`El archivo nombra ${plural(previo.tiposFaltantes.length, 'tipo', 'tipos')} que no existen`}
                >
                  {previo.tiposFaltantes.join(', ')}.
                  <Casilla
                    etiqueta="Crearlos al importar"
                    marcado={crearTipos}
                    onCambio={(valor) => {
                      setCrearTipos(valor)
                      if (listo) {
                        pedirPrevio({
                          mapeo: mapeo as MapeoEntidad,
                          hoja: lectura?.hoja ?? '',
                          crearTiposFaltantes: valor,
                        })
                      }
                    }}
                  />
                </Callout>
              )}

              {previo.rechazadas.length > 0 && (
                <DataTable
                  columns={columnasRechazo}
                  rows={[...previo.rechazadas]}
                  rowKey={(fila) => String(fila.fila)}
                  empty={{ title: 'Ninguna fila rechazada' }}
                />
              )}
            </>
          )}

          <div className="sigfisc-dialogo__acciones">
            <DialogClose asChild>
              <Button variant="secondary" type="button">
                Cancelar
              </Button>
            </DialogClose>
            <Button
              variant="primary"
              type="button"
              loading={confirmar.isPending || previsualizar.isPending || cargar.isPending}
              disabled={!entrada || previo === undefined || previo.aCrear.length === 0}
              onClick={() => {
                if (!entrada) return
                confirmar.mutate(entrada, {
                  onSuccess: (resumen) => {
                    avisarExito(
                      `${plural(resumen.entidades, 'entidad importada', 'entidades importadas')}` +
                        (resumen.canales > 0
                          ? `, con ${plural(resumen.canales, 'canal', 'canales')}`
                          : '') +
                        (resumen.tiposCreados > 0
                          ? ` y ${plural(resumen.tiposCreados, 'tipo nuevo', 'tipos nuevos')}`
                          : '') +
                        '.',
                    )
                    reiniciar()
                    onCambio(false)
                  },
                  onError: (error) => {
                    avisarError(error, 'No se pudo importar')
                  },
                })
              }}
            >
              <Upload size={16} aria-hidden="true" />
              {previo ? `Importar ${String(previo.aCrear.length)}` : 'Importar'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
